import axios from "axios";
import { BiSolidCameraMovie } from "react-icons/bi";
import { GiArtificialIntelligence } from "react-icons/gi";
import { GoLightBulb } from "react-icons/go";
import { HiOutlineAcademicCap } from "react-icons/hi";
import { IoBusiness, IoNewspaper } from "react-icons/io5";
import { MdAutoStories, MdHealthAndSafety, MdOutlineSportsCricket } from "react-icons/md";

/**
 * ------------------------------------------------------------------
 * BASE URL / CATEGORY CONFIG
 * ------------------------------------------------------------------
 * Every category has its own listing endpoint (given by the backend).
 * Only the LIST endpoints were provided, so this file assumes a
 * standard REST convention for the rest of the actions:
 *
 *   LIST     -> GET    {endpoint}
 *   SINGLE   -> GET    {endpoint}/{id}
 *   CREATE   -> POST   {endpoint}                (multipart/form-data)
 *   UPDATE   -> POST   {endpoint}/{id}            (multipart/form-data + _method=PUT, Laravel-style method spoofing so file uploads keep working)
 *   DELETE   -> DELETE {endpoint}/{id}
 *
 * If your Laravel routes are different (e.g. update uses a different
 * path, or status/trending have their own dedicated route), this is
 * the ONLY file you need to touch — everything else calls the
 * functions in articlesApi.js, not raw URLs.
 * ------------------------------------------------------------------
 */

export const CATEGORIES = [
  {
    key: "updates",
    label: "Updates",
    icon: <IoNewspaper />,
    endpoint: "https://todaytalks.mpdatahub.com/api/tech-updates",
  },
  {
    key: "business",
    label: "Business",
    icon: <IoBusiness />,
    endpoint: "https://todaytalks.mpdatahub.com/api/newsarticles",
  },
  {
    key: "education",
    label: "Education",
    icon: <HiOutlineAcademicCap />,
    endpoint: "https://todaytalks.mpdatahub.com/api/study-articles",
  },
  {
    key: "entertainment",
    label: "Entertainment",
    icon: <BiSolidCameraMovie />,
    endpoint: "https://todaytalks.mpdatahub.com/api/movienewsartcles",
  },
  {
    key: "health",
    label: "Health",
    icon: <MdHealthAndSafety />,
    endpoint: "https://todaytalks.mpdatahub.com/api/getHealthArticles",
  },
  {
    key: "insights",
    label: "Insights",
    icon: <GoLightBulb />,
    endpoint: "https://todaytalks.mpdatahub.com/api/getScientificFacts",
  },
  {
    key: "sports",
    label: "Sports",
    icon: <MdOutlineSportsCricket />,
    endpoint: "https://todaytalks.mpdatahub.com/api/getSportsArticles",
  },
  {
    key: "stories",
    label: "Stories",
    icon: <MdAutoStories />,
    endpoint: "https://todaytalks.mpdatahub.com/api/getShortStories",
  },
  {
    key: "technology",
    label: "Technology",
    icon: <GiArtificialIntelligence />,
    endpoint: "https://todaytalks.mpdatahub.com/api/technews",
  },
];

export const getCategoryByKey = (key) =>
  CATEGORIES.find((c) => c.key === key) || null;

export const AUTH_BASE_URL = "https://todaytalks.mpdatahub.com/api";

// Returns { total_active, category_counts: { <CategoryLabel>: count, ... } }
export const ACTIVE_COUNT_URL = "https://todaytalks.mpdatahub.com/api/talks/active-count";

/**
 * ------------------------------------------------------------------
 * WRITE ENDPOINTS (confirmed by backend validators)
 * ------------------------------------------------------------------
 * Unlike the per-category LIST endpoints above, create/update are a
 * single shared endpoint across all categories — `category` is sent
 * as a form field and must exactly match one of these 9 strings:
 * Insights, Technology, Business, Health, Education, Stories,
 * Updates, Sports, Entertainment. (The CATEGORIES labels above were
 * already written to match this exactly.)
 * ------------------------------------------------------------------
 */
export const CREATE_URL = "https://todaytalks.mpdatahub.com/api/talksstore";
export const updateUrl = (id) => `https://todaytalks.mpdatahub.com/api/talksupdate/${id}`;

/**
 * Dedicated status/trending toggle endpoints (confirmed by backend validators).
 * Both are GET requests — category + the new boolean value are sent as
 * query params, not a request body.
 *   GET https://todaytalks.mpdatahub.com/api/talks/{id}/status?category=...&status=1|0
 *   GET https://todaytalks.mpdatahub.com/api/talks/{id}/trending?category=...&trending=1|0
 */
export const statusToggleUrl = (id) => `https://todaytalks.mpdatahub.com/api/talks/${id}/status`;
export const trendingToggleUrl = (id) => `https://todaytalks.mpdatahub.com/api/talks/${id}/trending`;

// ⚠️ Not confirmed — guessed to match the talksstore/talksupdate naming
// convention. Update this if your real delete route is different.
export const deleteUrl = (id, category) => `https://todaytalks.mpdatahub.com/api/talksdelete/${id}?category=${category}`;

// Only these emails are allowed to log in (checked client-side as an
// extra layer, on top of whatever the backend already enforces).
export const ALLOWED_EMAILS = [
  "vishnubharani153@gmail.com",
  "kavinpriyagounder@gmail.com",
];

export const TOKEN_KEY = "news_admin_token";
export const USER_KEY = "news_admin_user";

const api = axios.create({
  timeout: 20000,
});

// Attach token to every request if we have one.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 (expired/invalid token).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default api;
